import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PremiumSubscription {
  id: string;
  user_id: string;
  plan_type: 'monthly' | 'yearly';
  status: 'active' | 'cancelled' | 'expired';
  amount: number;
  start_date: string;
  end_date: string;
  auto_renew: boolean;
  payment_method?: string;
}

export const usePremium = () => {
  const [isPremium, setIsPremium] = useState(false);
  const [subscription, setSubscription] = useState<PremiumSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      await checkPremiumStatus();

      // Only subscribe to realtime when authenticated
      if (user) {
        channel = supabase
          .channel('premium_subscriptions_changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'premium_subscriptions',
            },
            () => {
              checkPremiumStatus();
            }
          )
          .subscribe();
      }
    };

    init();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsPremium(false);
        setSubscription(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('premium_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking premium status:', error);
        setIsPremium(false);
        setSubscription(null);
      } else if (data) {
        const isActive = data.status === 'active' && new Date(data.end_date) > new Date();
        setIsPremium(isActive);
        setSubscription(data as PremiumSubscription);
      } else {
        setIsPremium(false);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error in checkPremiumStatus:', error);
      setIsPremium(false);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPremium = async (planType: 'monthly' | 'yearly', paymentReference: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to subscribe to Premium",
          variant: "destructive",
        });
        return false;
      }

      if (!paymentReference) {
        toast({
          title: "Payment Required",
          description: "Please complete payment before subscribing",
          variant: "destructive",
        });
        return false;
      }

      const { data, error } = await supabase.functions.invoke('subscribe-premium', {
        body: {
          plan_type: planType,
          payment_reference: paymentReference,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "🎉 Welcome to LUMZA Premium!",
        description: "Your subscription is now active. Enjoy your exclusive benefits!",
      });

      await checkPremiumStatus();
      return true;
    } catch (error) {
      console.error('Error subscribing to premium:', error);
      toast({
        title: "Subscription Failed",
        description: "There was an error processing your subscription. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const cancelSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to manage your subscription",
          variant: "destructive",
        });
        return false;
      }

      // Cancellation still needs a server-side function in production,
      // but since it only sets status to 'cancelled' (not granting access),
      // it's lower risk. For now, we use an edge function call too.
      const { data, error } = await supabase.functions.invoke('subscribe-premium', {
        body: {
          action: 'cancel',
        },
      });

      if (error) throw error;

      toast({
        title: "Subscription Cancelled",
        description: "Your premium benefits will remain active until the end of your billing period.",
      });

      await checkPremiumStatus();
      return true;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: "Cancellation Failed",
        description: "There was an error cancelling your subscription. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    isPremium,
    subscription,
    loading,
    subscribeToPremium,
    cancelSubscription,
    checkPremiumStatus,
  };
};
